import { BufferAttribute, BufferGeometry, Color, Mesh, ShaderMaterial, Vector2 } from 'three'
import aastep from 'glsl-aastep/index.glsl'
import { Easing, Tween } from '@tweenjs/tween.js'
import { tweenGroup } from './tweenGroup'
import { EventEmitter } from './EventEmitter'
import { BodyColor, COLORS } from '@/const'
import { delay } from '@/utils/delay'
import App from './App'

class Fade extends EventEmitter {
	private readonly object: Mesh<BufferGeometry, ShaderMaterial>

	private readonly app: App
	private readonly material: ShaderMaterial
	private readonly tweens: Tween<{ elapsed: number }>[] = []

	constructor(app: App, color: BodyColor) {
		super()
		this.app = app

		const { width, height, pixelRatio } = this.app.sizes

		this.material = new ShaderMaterial({
			transparent: true,
			depthWrite: false,
			depthTest: false,
			//language=GLSL
			vertexShader: `
				precision highp float;

				void main() {
					gl_Position = vec4(position.xy, 1.0, 1.0);
				}
			`,
			//language=GLSL
			fragmentShader: `
				precision highp float;

				uniform float elapsed;
				uniform vec3 color;
				uniform vec2 resolution;

				${aastep}

				float sdRoundedBox(in vec2 p, in vec2 b, in vec4 r){
					r.xy = (p.x>0.0)?r.xy : r.zw;
					r.x  = (p.y>0.0)?r.x  : r.y;
					vec2 q = abs(p)-b+r.x;
					return min(max(q.x, q.y), 0.0) + length(max(q, 0.0)) - r.x;
				}

				void main() {
					float aspect = resolution.y / resolution.x;
					vec2 st = gl_FragCoord.xy/resolution.x + vec2(-0.5, -0.5 * aspect);
					float d = sdRoundedBox(st, vec2(elapsed, elapsed * aspect), vec4(elapsed * (aspect < 1.0 ? 0.5: 1.0)));
					float c = aastep(0.0, d);
					gl_FragColor = vec4(color, c);
				}
			`,
			uniforms: {
				color: { value: new Color(color) },
				alpha: { value: 0.5 },
				resolution: { value: new Vector2(Math.round(width * pixelRatio), Math.round(height * pixelRatio)) },
				radius: { value: 0.5 },
				pos: { value: -0.5 },
				elapsed: { value: 0.5 }
			}
		})

		const geometry = new BufferGeometry()
		const vertices = new Float32Array([-1.0, -1.0, 0.0, 3.0, -1.0, 0.0, -1.0, 3.0, 0.0])
		geometry.setAttribute('position', new BufferAttribute(vertices, 3))

		this.object = new Mesh(geometry, this.material)
		this.object.renderOrder = 1000
		this.object.frustumCulled = false
		this.object.visible = false

		this.app.scene.add(this.object)
	}

	resize() {
		const { width, height, pixelRatio } = this.app.sizes
		this.material.uniforms.resolution.value.set(Math.round(width * pixelRatio), Math.round(height * pixelRatio))
	}

	async show() {
		this.tweens.forEach((tween) => tween.stop())
		this.tweens.length = 0

		const colors = Object.values(COLORS)
		const color = colors[Math.floor(Math.random() * colors.length)]

		this.tweens.push(
			new Tween({ elapsed: 1 }, tweenGroup)
				.to({ elapsed: 0.475 }, 650)
				.easing(Easing.Exponential.InOut)
				.onUpdate(({ elapsed }) => {
					this.material.uniforms.elapsed.value = elapsed
					this.app.requestRender()
				})
				.onStart(() => {
					this.material.uniforms.color.value.setStyle(color)
					this.object.visible = true
				})
				.start()
		)

		await delay(650)

		this.tweens.push(
			new Tween({ elapsed: 0.475 }, tweenGroup)
				.to({ elapsed: 0 }, 400)
				.easing(Easing.Exponential.In)
				.onUpdate(({ elapsed }) => {
					this.material.uniforms.elapsed.value = elapsed
					this.app.requestRender()
				})
				.start()
		)

		await delay(650)
	}

	async hide() {
		this.tweens.forEach((tween) => tween.stop())
		this.tweens.length = 0

		this.tweens.push(
			new Tween({ elapsed: 0 }, tweenGroup)
				.to({ elapsed: 1 }, 500)
				.easing(Easing.Exponential.In)
				.onUpdate(({ elapsed }) => {
					this.material.uniforms.elapsed.value = elapsed
					this.app.requestRender()
				})
				.onComplete(() => (this.object.visible = false))
				.onStart(() => (this.object.visible = true))
				.start()
		)

		await delay(500)
	}
}

export { Fade }
